import { css } from 'astroturf';
import React, { useEffect, useState } from 'react';
import { Feed } from 'react-bytesize-icons';
import NewsletterForm from './newsletterForm';

const styles = css`
  .subscribe {
    margin-top: 1rem;
    text-align: center;
    display:flex;
    align-items: stretch;
    flex-direction:column;
    gap:0.5rem;
  }
  .subscribe div {
    margin: 0;
    text-align: center;
    display: flex;
    gap: 1rem;

    & a {
      margin: 0;
      flex: 1 1 auto;
      background: none;
      box-shadow: initial;
      border: 1px solid;
      padding: 0.5rem;
      border-radius: 40px;
      line-height: 1;
      vertical-align: middle;
      display: flex;
      align-content: center;
      justify-content: center;
      gap: 0.5rem;
      transition: 0.25s ease-in-out background;

      &:hocus {
        background: rgb(0, 121, 177, 0.1);
      }
      &:last-child {
        color: rgb(197, 77, 0);
        &:hocus {
          background: rgb(197, 77, 0, 0.1);
        }
      }

      & svg {
        vertical-align: middle;
      }
    }
  }
`;

function getIds(items) {
  return items.reduce((acc, item) => {
    if (item.url) {
      // url has a # as first character, remove it to get the raw CSS-id
      acc.push(item.url.slice(1));
    }
    if (item.items) {
      acc.push(...getIds(item.items));
    }
    return acc;
  }, []);
}

function useActiveId(itemIds) {
  const [activeId, setActiveId] = useState(`test`);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: `70px 0% -50% 0%` }
    );
    itemIds.forEach((id) => {
      observer.observe(document.getElementById(id));
    });
    return () => {
      itemIds.forEach((id) => {
        const element = document.getElementById(id);
        if (element) {
          observer.unobserve(document.getElementById(id));
        }
      });
    };
  }, [itemIds]);
  return activeId;
}
function childHasActiveId(child, activeId) {
  if (child.url.slice(1) === activeId) {
    return true;
  }

  if (child.items?.length > 0) {
    return child.items.filter((childItem) => childHasActiveId(childItem, activeId)).length;
  }

  return false;
}

function hasActive(activeId, item, children) {
  if (activeId === item) {
    return true;
  }

  if (children) {
    return children.filter((child) => childHasActiveId(child, activeId)).length;
  }

  return false;
}

function renderItems(items, activeId) {
  return (
    <ol>
      {items.map((item) => (
        <li key={item.url} className={hasActive(activeId, item.url.slice(1), item.items) ? 'active' : undefined}>
          {item.items ? (
            <details open={hasActive(activeId, item.url.slice(1), item.items)}>
              <summary>
                <a
                  href={item.url}
                  className={hasActive(activeId, item.url.slice(1), item.items) ? 'active' : undefined}
                >
                  {item.title}
                </a>
              </summary>

              {item.items && renderItems(item.items, activeId)}
            </details>
          ) : (
            <a href={item.url} className={hasActive(activeId, item.url.slice(1)) ? 'active' : undefined}>
              {item.title}
            </a>
          )}
        </li>
      ))}
    </ol>
  );
}
function TableOfContents(props) {
  const idList = getIds(props.items);
  const activeId = useActiveId(idList);
  return (
    <>
      <div className="table-of-contents">
        <div className="contents-wrapper">
          <p>
            <strong>Table of contents</strong>
          </p>
          <a className="sr-only" href="#article">
            Skip table of contents
          </a>
          {renderItems(props.items, activeId)}
        </div>
      </div>
      <div className="blog-cta">
        <div className="contents-wrapper">
          <p>
            <strong>If you want to be kept up to date with new articles, CSS resources and tools,</strong> join our
            newsletter.
          </p>
          <NewsletterForm />

          <div className={styles.subscribe}>
            <strong>Or subscribe elsewhere:</strong>
            <div>
              <a href="https://twitter.com/intent/user?screen_name=polypane">
                <svg
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width={16}
                  height={16}
                  fill="hsl(200, 56.33%, 55.1%)"
                >
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                </svg>{' '}
                @Polypane
              </a>
              <a href="/rss.xml">
                <Feed width={16} height={16} color="#f60" strokeWidth={4} /> RSS
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default TableOfContents;
